import secureLocalStorage from "react-secure-storage";

export const STORAGE_KEY = "wordpress-user";
export const AUTH_EVENT = "ui-auth-updated";

// Guest mode: a logged-out visitor who is using free features without an
// account. Stored as a simple flag (NOT a token) so it is distinct from a
// real logged-in session. Cleared automatically once they log in or register.
export const GUEST_KEY = "ui-guest-mode";

export const dispatchAuthEvent = () => {
  if (typeof globalThis !== "undefined" && globalThis.dispatchEvent) {
    globalThis.dispatchEvent(new Event(AUTH_EVENT));
  }
};

export const persistAuthPayload = (payload: {
  user: any;
  purchases: any[];
  /** Effective supporter perks, resolved server-side. Optional so older stored
   * payloads and callers that do not have them still type-check; a missing list
   * reads as no perks, which fails closed. */
  perks?: string[];
}) => {
  // Several callers re-persist the session without perks (refreshLocalData, the
  // x-new-token refresh). Writing their payload as-is wiped the stored perks, and
  // the auth event then reloaded state with none, so a perk-only member lost the
  // List again on the next refresh. Keep what is stored unless a caller says
  // otherwise. Task #3073.
  const perks = payload.perks ?? readStoredPerks();
  secureLocalStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(perks ? { ...payload, perks } : payload)
  );
  dispatchAuthEvent();
};

function readStoredPerks(): string[] | undefined {
  try {
    const raw = secureLocalStorage.getItem(STORAGE_KEY);
    if (typeof raw !== "string") return undefined;
    const stored = JSON.parse(raw);
    return Array.isArray(stored?.perks) ? stored.perks : undefined;
  } catch {
    return undefined;
  }
}

export const clearAuthPayload = () => {
  secureLocalStorage.removeItem(STORAGE_KEY);
  dispatchAuthEvent();
};

export const enterGuestMode = () => {
  secureLocalStorage.setItem(GUEST_KEY, "1");
  dispatchAuthEvent();
};

export const exitGuestMode = () => {
  secureLocalStorage.removeItem(GUEST_KEY);
};

export const isGuestMode = (): boolean => {
  return !!secureLocalStorage.getItem(GUEST_KEY);
};

export function getAuthScheme(token: string) {
  return token.includes(".") ? "JWT" : "Invite";
}

const BASE64_LOOKUP: number[] = (() => {
  const lookup = new Array(256).fill(-1);
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  for (let i = 0; i < alphabet.length; i += 1) {
    lookup[alphabet.codePointAt(i) ?? 0] = i;
  }
  return lookup;
})();

const textDecoder =
  typeof globalThis !== "undefined" && "TextDecoder" in globalThis
    ? new globalThis.TextDecoder()
    : undefined;

function decodeBase64(input: string) {
  const sanitized = input.replaceAll(/[^A-Za-z0-9+/=]/g, "");
  const output = new Uint8Array(Math.floor((sanitized.length * 3) / 4));
  let buffer = 0;
  let bitsCollected = 0;
  let outIndex = 0;

  for (let idx = 0; idx < sanitized.length; idx += 1) {
    const charCode = sanitized.codePointAt(idx) ?? -1;
    if (sanitized[idx] === "=") {
      break;
    }
    const value = BASE64_LOOKUP[charCode];
    if (value < 0) {
      continue;
    }
    buffer = (buffer << 6) | value;
    bitsCollected += 6;
    if (bitsCollected >= 8) {
      bitsCollected -= 8;
      output[outIndex] = (buffer >> bitsCollected) & 0xff;
      outIndex += 1;
    }
  }

  const bytes = output.subarray(0, outIndex);
  if (textDecoder) {
    return textDecoder.decode(bytes);
  }
  let fallback = "";
  for (const byte of bytes) {
    fallback += String.fromCodePoint(byte);
  }
  return fallback;
}

export function isTokenExpired(token: string) {
  // Opaque invite tokens are managed server-side; treat them as not expired client-side
  if (!token.includes(".")) return false;
  try {
    // JWT tokens are base64Url encoded
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replaceAll("-", "+").replaceAll("_", "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const jsonPayload = decodeBase64(padded);
    const { exp } = JSON.parse(jsonPayload);
    if (!exp) {
      // Token doesn't have an expiration date, consider it expired
      return true;
    }

    const currentTimestamp = Math.floor(Date.now() / 1000); // in seconds
    return exp < currentTimestamp;
  } catch (e) {
    console.warn("Token decode failed, treating as expired", e);
    return true;
  }
}
